// Mesaj metnindeki bağlantıları bulup tıklanabilir parçalara ayırır.
//
// GÜVENLİK SÖZLEŞMESİ — bu dosyanın asıl işi budur. Uygulamanın başka
// yerlerindeki `Linking.openURL` çağrıları hep UYGULAMANIN KENDİ ürettiği
// adresleri açar (OAuth, Stripe). Burası ilk kez BAŞKA BİR KULLANICININ yazdığı
// adresi açıyor; ücretli mesajlaşmada karşı taraf tanımadığın biri olabildiği
// için varsayılanlar dar tutuldu:
//
//  1. Yalnız metnin KENDİSİ bağlantı olduğunda bağlantı çizilir. "Şuraya bak"
//     gibi bir metnin arkasına gizli adres koyma yolu yoktur — gördüğün şey
//     gittiğin yerdir. Kimlik avının en ucuz biçimi budur ve yapısal olarak
//     kapalıdır.
//  2. Yalnız `http` ve `https`. `javascript:`, `file:`, `intent:` ve uygulama
//     şemaları düz metin kalır; bir mesajın uygulama içi eylem tetiklemesi
//     mümkün olmamalıdır.
//  3. `www.` ile başlayan adres `https` sayılır — `http`e düşürmek, kullanıcıyı
//     sessizce şifresiz bağlantıya göndermek olurdu.
//
// Bağlantı açılmadan önce hedefin gösterilmesi çağıranın işidir (bkz.
// MessageScreen); bu modül yalnız neyin bağlantı SAYILDIĞINA karar verir.

export type MessageSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; url: string };

// Boşluk görene kadar süren aday. Şema ya açıkça http(s) ya da `www.` olmalı;
// gerisi düz metindir.
const CANDIDATE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

// Cümle sonu noktalaması adrese yapışır: "site.com." → nokta cümlenin. Kapanan
// parantez yalnız açılışı yoksa atılır, çünkü bazı adresler parantez taşır
// (Wikipedia gibi).
function trimTrailing(raw: string): string {
  let value = raw;
  for (;;) {
    const last = value.slice(-1);
    if ('.,!?;:'.includes(last)) { value = value.slice(0, -1); continue; }
    if (last === ')' && !value.includes('(')) { value = value.slice(0, -1); continue; }
    if (last === ']' && !value.includes('[')) { value = value.slice(0, -1); continue; }
    break;
  }
  return value;
}

/** Görünen adresten açılacak adresi üretir; şema yoksa https varsayılır. */
function toUrl(value: string): string | null {
  const url = /^www\./i.test(value) ? `https://${value}` : value;
  if (!/^https?:\/\//i.test(url)) return null;
  // Şemadan sonra en az bir karakter host olmalı: "http://" tek başına adres değil.
  return /^https?:\/\/[^/\s]+/i.test(url) ? url : null;
}

/**
 * Metni düz yazı ve bağlantı parçalarına ayırır. Bağlantı yoksa tek parça döner,
 * yani çağıran "bağlantı var mı" diye ayrıca sormak zorunda kalmaz.
 */
export function splitMessageLinks(text: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(CANDIDATE)) {
    const start = match.index ?? 0;
    const visible = trimTrailing(match[0]);
    const url = toUrl(visible);
    if (!url) continue;

    if (start > cursor) segments.push({ type: 'text', value: text.slice(cursor, start) });
    segments.push({ type: 'link', value: visible, url });
    cursor = start + visible.length;
  }

  if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });
  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
}

/**
 * Onay penceresinde gösterilecek ana makine adı. Kullanıcının gerçekte NEREYE
 * gittiğini görmesi için adresin tamamı değil host'u öne çıkarılır: uzun bir
 * adreste asıl alan adı gözden kaçabilir.
 */
export function linkHost(url: string): string {
  const match = /^https?:\/\/([^/?#\s]+)/i.exec(url);
  if (!match) return url;
  // `https://google.com@kotu.example/` adresinin gerçek host'u `kotu.example`:
  // `@` öncesi kullanıcı bilgisidir. Onu göstermek kimlik avının ta kendisi
  // olurdu (9.32 · D-653). Port da adın parçası değil.
  const authority = match[1];
  const host = authority.slice(authority.lastIndexOf('@') + 1).replace(/:\d*$/, '');
  return host.replace(/^www\./i, '').toLowerCase();
}

/**
 * Bu adres için önizleme hazırlanabilir mi (9.32 · D-653)?
 *
 * Yalnız `https`: düz `http` ile gelen bir sayfayı yoldaki herkes değiştirebilir
 * ve önizleme kartı yalan söyler. Yalnız ASCII alan adı: Kiril `а` ile yazılmış
 * `аpple.com` gözle `apple.com`dan ayırt edilemez, kısa görünüm de tam olarak
 * alan adına güveniyor. IP adresi ve yerel ad da yok: göndereni kendi ağındaki
 * bir cihaza istek atmaya yönlendirmenin anlamı yok.
 */
export function previewableUrl(url: string): boolean {
  if (!/^https:\/\//i.test(url)) return false;
  const host = linkHost(url);
  if (!/^[a-z0-9.-]+$/.test(host) || !host.includes('.')) return false;
  if (/^[\d.]+$/.test(host)) return false;
  return !/(^|\.)(localhost|local|internal|lan|home|arpa)$/.test(host);
}

/** Metindeki ilk önizlenebilir link; yoksa null (9.32 · D-653). */
export function firstPreviewableLink(text: string): string | null {
  for (const segment of splitMessageLinks(text)) {
    if (segment.type === 'link' && previewableUrl(segment.url)) return segment.url;
  }
  return null;
}

/**
 * Önizlemesi gösterilen linki metinden çıkar: kısa görünüm ve büyük kart linkin
 * YERİNE geçiyor. Link metinde bulunamazsa (ör. çeviri değiştirdiyse) metin
 * olduğu gibi kalır; adres görünmeye devam eder, kaybolmaz.
 */
export function withoutLink(text: string, url: string): string {
  const segments = splitMessageLinks(text);
  const index = segments.findIndex((s) => s.type === 'link' && s.url === url);
  if (index < 0) return text;
  const before = segments.slice(0, index).map((s) => s.value).join('').replace(/[ \t]+$/, '');
  const after = segments.slice(index + 1).map((s) => s.value).join('').replace(/^[ \t]+/, '');
  const joined = before && after && !/\n$/.test(before) && !/^\n/.test(after) ? `${before} ${after}` : before + after;
  return joined.trim();
}
